import './Generate.scss';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { Overlay, OverlayTrigger, Popover } from 'react-bootstrap';
import { MdAutoAwesome, MdClose, MdInfoOutline } from 'react-icons/md';
import { AdPlatform, InstanceRow, StyleGuideRow } from '@blisspointmedia/bpm-types/dist/CopyTiger';
import { Button } from '../../Components/Button';
import { Appearance, Design, Intent, Placement, Size } from '../../Components/designConstants';
import { Dropdown } from '../../Components/Dropdown';
import { IconButton } from '../../Components/IconButton';
import { InteractiveDiv } from '../../Components/InteractiveEl';
import { FullPageSpinner, Spinner } from '../../Components/Spinner';
import { BPMTable } from '../../Components/StickyTable';
import { useAppSelector } from '../../redux';
import { useSetError } from '../../redux/modals';
import * as UserRedux from '../../redux/user';
import { download } from '../../utils/download-utils';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useIsTruncated } from '../../utils/hooks/useIsTruncated';
import { Mixpanel, MxE } from '../../utils/mixpanelWrapper';
import { PAGE_NAMES } from '../../Components/Page/page-constants';
import { conditionallyPluralizeWithCount } from '../../utils/text-utils';
import { CREATIVITY_OPTIONS } from './creativity-options';
import { CopyTigerAlert } from './CopyTigerAlert';
import {
  DialogState,
  formatLastModified,
  generateCopyTigerAds,
  getCopyTigerModelInstances,
  getCopyTigerStyleGuides,
  KeywordsData,
  KeywordsMap,
  PLATFORM_ID_TOKEN,
  PLATFORM_LABELS,
  TableRow
} from './copyTigerUtils';

// Reverse of PLATFORM_ID_TOKEN: maps the id prefix token ('google' | 'bing') back to a platform, so
// adsUsed ids can be decoded. Legacy ids (pre-platform) have no token and start with the account id.
const TOKEN_TO_PLATFORM: Record<string, AdPlatform> = Object.fromEntries(
  (Object.keys(PLATFORM_ID_TOKEN) as AdPlatform[]).map(platform => [
    PLATFORM_ID_TOKEN[platform],
    platform
  ])
);

const CDN = 'https://cdn.blisspointmedia.com';
const GOOGLE_GEMINI_LOGO = `${CDN}/assets/img/Gemini.png`;

interface GenerateProps {
  isLoadingTrainingOptions: boolean;
  trainingOptions: TableRow[];
}

export const Generate = ({ isLoadingTrainingOptions, trainingOptions }: GenerateProps) => {
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);
  const [keywordsData, setKeywordsData] = useState<KeywordsData[]>([]);
  const [models, setModels] = useState<InstanceRow[]>();
  const [selectedAccountIDs, setSelectedAccountIDs] = useState<Record<string, string>>({});
  const [selectedAdGroupIDs, setSelectedAdGroupIDs] = useState<Record<string, string>>({});
  const [selectedCampaignIDs, setSelectedCampaignIDs] = useState<Record<string, string>>({});
  const [selectedPlatforms, setSelectedPlatforms] = useState<Record<string, string>>({});
  const [selectedCreativity, setSelectedCreativity] = useState('Balanced');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedStyleGuide, setSelectedStyleGuide] = useState('');
  const [showModelModal, setShowModelModal] = useState<string | null>(null);
  const [styleGuides, setStyleGuides] = useState<StyleGuideRow[]>([]);
  const [dialog, setDialog] = useState<DialogState>();

  const userEmail = useAppSelector(UserRedux.emailSelector);
  const companyInfo = useCompanyInfo();
  const setError = useSetError();
  const company = companyInfo?.cid;

  const keywordsMap = useMemo(() => {
    const map: KeywordsMap = {};
    if (!trainingOptions) return map;

    for (const row of trainingOptions) {
      if (map[row.accountID] == null) {
        map[row.accountID] = {
          accountName: row.accountName,
          platform: row.platform,
          campaigns: {
            [row.campaignID]: {
              campaignName: row.campaignName,
              adGroups: {
                [row.adGroupID]: {
                  adGroupName: row.adGroupName,
                  keywords: row.keywords
                }
              }
            }
          }
        };
      } else if (map[row.accountID].campaigns[row.campaignID] == null) {
        map[row.accountID].campaigns[row.campaignID] = {
          campaignName: row.campaignName,
          adGroups: {
            [row.adGroupID]: {
              adGroupName: row.adGroupName,
              keywords: row.keywords
            }
          }
        };
      } else if (map[row.accountID].campaigns[row.campaignID].adGroups[row.adGroupID] == null) {
        map[row.accountID].campaigns[row.campaignID].adGroups[row.adGroupID] = {
          adGroupName: row.adGroupName,
          keywords: row.keywords
        };
      }
    }
    return map;
  }, [trainingOptions]);

  const [accountIDMap, campaignIDMap, adGroupIDMap, platformIDMap] = useMemo(() => {
    const acctMap: Record<string, string> = {};
    const campMap: Record<string, string> = {};
    const adGrpMap: Record<string, string> = {};
    const platMap: Record<string, string> = {};
    if (keywordsMap) {
      const hasPlatformFilter = Object.keys(selectedPlatforms).length > 0;
      const hasAccountFilter = Object.keys(selectedAccountIDs).length > 0;
      const hasCampaignFilter = Object.keys(selectedCampaignIDs).length > 0;
      for (const accountID of Object.keys(keywordsMap)) {
        const { platform } = keywordsMap[accountID];
        platMap[platform] = PLATFORM_LABELS[platform] ?? platform;
        // Platform is the top of the hierarchy: when platforms are selected, only surface accounts
        // (and their campaigns/ad groups) for those platforms.
        if (hasPlatformFilter && selectedPlatforms[platform] == null) {
          continue;
        }
        acctMap[accountID] = keywordsMap[accountID].accountName;
        if (hasAccountFilter && selectedAccountIDs[accountID] == null) {
          continue;
        }
        for (const campaignID of Object.keys(keywordsMap[accountID].campaigns)) {
          campMap[campaignID] = keywordsMap[accountID].campaigns[campaignID].campaignName;
          if (hasCampaignFilter && selectedCampaignIDs[campaignID] == null) {
            continue;
          }
          for (const adGroupID of Object.keys(
            keywordsMap[accountID].campaigns[campaignID].adGroups
          )) {
            adGrpMap[adGroupID] =
              keywordsMap[accountID].campaigns[campaignID].adGroups[adGroupID].adGroupName;
          }
        }
      }
    }
    return [acctMap, campMap, adGrpMap, platMap];
  }, [keywordsMap, selectedPlatforms, selectedAccountIDs, selectedCampaignIDs]);

  const selectedKeywords = useMemo<KeywordsData[]>(() => {
    if (!keywordsMap) return [];
    const result: KeywordsData[] = [];
    const hasPlatformFilter = Object.keys(selectedPlatforms).length > 0;
    for (const accountID of Object.keys(keywordsMap)) {
      if (selectedAccountIDs[accountID] == null) continue;
      const account = keywordsMap[accountID];
      if (hasPlatformFilter && selectedPlatforms[account.platform] == null) {
        continue;
      }
      for (const campaignID of Object.keys(account.campaigns)) {
        if (selectedCampaignIDs[campaignID] == null) continue;
        const campaign = account.campaigns[campaignID];
        for (const adGroupID of Object.keys(campaign.adGroups)) {
          if (selectedAdGroupIDs[adGroupID] == null) continue;
          const adGroup = campaign.adGroups[adGroupID];
          result.push({
            'Account ID': accountID,
            'Account Name': account.accountName,
            'Campaign ID': campaignID,
            'Campaign Name': campaign.campaignName,
            'Ad Group ID': adGroupID,
            'Ad Group Name': adGroup.adGroupName,
            Platform: PLATFORM_LABELS[account.platform] ?? account.platform,
            keywords: adGroup.keywords
          });
        }
      }
    }
    return result;
  }, [keywordsMap, selectedPlatforms, selectedAccountIDs, selectedCampaignIDs, selectedAdGroupIDs]);

  useEffect(() => {
    setKeywordsData(selectedKeywords);
  }, [selectedKeywords]);

  const createModelTableData = useCallback((model: InstanceRow) => {
    const tableHeaders = [
      { name: 'index', label: '#', flex: 0, width: 50 },
      { name: 'platform', label: 'Platform', flex: 1 },
      { name: 'accountID', label: 'Account ID', flex: 1 },
      { name: 'campaignID', label: 'Campaign ID', flex: 1 },
      { name: 'adGroupID', label: 'Ad Group ID', flex: 1 },
      { name: 'keywords', label: 'Keywords', flex: 2 }
    ];
    const adsUsedArray = model.adsUsed ?? [];
    const tableData =
      adsUsedArray.length > 0
        ? adsUsedArray.map((encodedID, index) => {
            const parts = encodedID.split('_');
            // New ids are prefixed with a platform token ('google' | 'bing'); legacy ids start with the
            // account id and predate platform support (treated as Google Ads).
            const platform = TOKEN_TO_PLATFORM[parts[0]];
            const offset = platform ? 1 : 0;
            const platformLabel = PLATFORM_LABELS[platform ?? 'google_ads'];
            if (parts.length - offset >= 3) {
              return {
                index: index + 1,
                platform: platformLabel,
                accountID: parts[offset],
                campaignID: parts[offset + 1],
                adGroupID: parts[offset + 2],
                keywords: parts.slice(offset + 3)
              };
            }
            return {
              index: index + 1,
              platform: platformLabel,
              campaignID: 'Unknown',
              adGroupID: 'Unknown',
              keywords: encodedID
            };
          })
        : [
            {
              index: 0,
              campaignID: 'No ads found',
              adGroupID: 'in this model',
              keywords: 'instance'
            }
          ];
    return { headers: tableHeaders, data: tableData };
  }, []);

  const removeKeyword = useCallback((keywordToRemove: string) => {
    setKeywordsData(prev =>
      prev.map(entry => ({
        ...entry,
        keywords: entry.keywords.filter(keyword => keyword !== keywordToRemove)
      }))
    );
  }, []);

  const generate = useCallback(() => {
    (async () => {
      if (!selectedModel) {
        setDialog({
          body: 'Please select a model.',
          title: 'Error',
          intent: Intent.ERROR
        });
        return;
      }
      if (keywordsData.length === 0) {
        setDialog({
          body: 'Please add keywords',
          title: 'Error',
          intent: Intent.ERROR
        });
        return;
      }
      setIsLoading(true);
      try {
        const selectedStyleGuideData = styleGuides.find(sg => sg.id === selectedStyleGuide);
        if (selectedStyleGuide && selectedStyleGuideData == null) {
          throw new Error('Selected style guide not found');
        }
        const selectedModelData = (models ?? []).find(m => m.id === selectedModel);
        if (selectedModelData == null) {
          throw new Error('Selected model not found');
        }
        const { settings: creativitySettings } =
          CREATIVITY_OPTIONS[selectedCreativity] ?? CREATIVITY_OPTIONS.Balanced;
        const generatedAds = await generateCopyTigerAds({
          additionalInstructions: additionalInstructions.trim(),
          company,
          email: userEmail,
          instance_uuid: selectedModelData.id,
          keywordsData,
          styleGuide_uuid: selectedStyleGuideData?.id,
          temperature: creativitySettings.temperature,
          top_k: creativitySettings.top_k,
          top_p: creativitySettings.top_p
        });
        if (generatedAds.error) {
          throw new Error(generatedAds.error);
        }
        try {
          let csvContent = '';
          for (let rowIndex = 0; rowIndex < generatedAds.generatedAdsData.length; rowIndex++) {
            const ad = generatedAds.generatedAdsData[rowIndex];
            const fields = Object.keys(ad);
            if (rowIndex === 0) {
              csvContent = `${fields.join('\t')}\n`;
            }
            csvContent += `${fields.map(field => ad[field]).join('\t')}\n`;
          }
          download(csvContent, 'copytigerGeneratedAds.csv', 'text/csv');
          setDialog({
            body: 'Ads generated successfully! Open CSV with \\t (tab character) as the delimiter.',
            title: 'Generated Ads!',
            intent: Intent.SUCCESS
          });
          Mixpanel.track(MxE.ActionTriggered, {
            page: PAGE_NAMES.ADCOPY_AI,
            action: 'generate_copy'
          });
        } catch (e) {
          throw new Error(
            `Error trying to export to csv: ${e.message}. Check your email for a json file with your ads. Check your spam folder if it's not in your inbox.`,
            { cause: e }
          );
        }
      } catch (e) {
        setError({ message: e.message, reportError: e });
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    additionalInstructions,
    company,
    keywordsData,
    models,
    selectedCreativity,
    selectedModel,
    selectedStyleGuide,
    setError,
    styleGuides,
    userEmail
  ]);

  useEffect(() => {
    (async () => {
      if (company && !isInitialLoadDone) {
        try {
          setStyleGuides(await getCopyTigerStyleGuides(company));
        } catch (e) {
          setError({
            message: `Failed to fetch AdCopy AI style guides for ${company}. Error: ${e.message}`,
            reportError: e
          });
        }
        try {
          const fetchedModels = await getCopyTigerModelInstances(company);
          setModels(fetchedModels);
        } catch (e) {
          setError({
            message: `Failed to fetch AdCopy AI Models for ${company}. Error: ${e.message}`,
            reportError: e
          });
        } finally {
          setIsInitialLoadDone(true);
        }
      }
    })();
  }, [company, isInitialLoadDone, setError]);

  const uniqueKeywords = useMemo(
    () => [...new Set(keywordsData.flatMap(data => data.keywords))],
    [keywordsData]
  );

  return (
    <>
      {(!isInitialLoadDone || isLoading) && <FullPageSpinner transparent={isInitialLoadDone} />}
      <div className='copytiger-generate-widget' data-testid='generate-page'>
        <div className='copytiger-generate-widget-container'>
          <div className='copytiger-generate-widget-header'>
            <div className='title-container'>
              <div className='copytiger-generate-widget-title'>Generate Ads with AdCopy AI</div>
              <div className='info-container'>
                Use Google&apos;s AI technology to streamline ideation, enable structured A/B
                testing, and enhance performance outcomes.
              </div>
            </div>
            <div className='powered-by-container'>
              <div className='powered-by-text'>Powered by:</div>
              <img src={GOOGLE_GEMINI_LOGO} alt='Google Gemini Logo' className='google-logo' />
            </div>
          </div>
          <div className='copytiger-generate-widget-body'>
            <div className='column-container'>
              <ModelColumn
                company={company}
                models={models}
                selectedModel={selectedModel}
                setSelectedModel={setSelectedModel}
                setShowModelModal={setShowModelModal}
              />
              <StyleGuideColumn
                company={company}
                selectedStyleGuide={selectedStyleGuide}
                setSelectedStyleGuide={setSelectedStyleGuide}
                styleGuides={styleGuides}
              />
              <CreativityColumn
                additionalInstructions={additionalInstructions}
                selectedCreativity={selectedCreativity}
                setAdditionalInstructions={setAdditionalInstructions}
                setSelectedCreativity={setSelectedCreativity}
              />
              <KeywordColumn
                accountIDMap={accountIDMap}
                adGroupIDMap={adGroupIDMap}
                campaignIDMap={campaignIDMap}
                isLoadingTrainingOptions={isLoadingTrainingOptions}
                keywordsData={keywordsData}
                platformIDMap={platformIDMap}
                removeKeyword={removeKeyword}
                selectedAccountIDs={selectedAccountIDs}
                selectedAdGroupIDs={selectedAdGroupIDs}
                selectedCampaignIDs={selectedCampaignIDs}
                selectedPlatforms={selectedPlatforms}
                setSelectedAccountIDs={setSelectedAccountIDs}
                setSelectedAdGroupIDs={setSelectedAdGroupIDs}
                setSelectedCampaignIDs={setSelectedCampaignIDs}
                setSelectedPlatforms={setSelectedPlatforms}
                uniqueKeywords={uniqueKeywords}
              />
            </div>
          </div>
          <div className='copytiger-generate-widget-footer'>
            <Button
              size={Size.LG}
              onClick={() => {
                setAdditionalInstructions('');
                setKeywordsData([]);
                setSelectedAccountIDs({});
                setSelectedAdGroupIDs({});
                setSelectedCampaignIDs({});
                setSelectedCreativity('Balanced');
                setSelectedModel('');
                setSelectedStyleGuide('');
              }}
              appearance={Appearance.OUTLINED}
              design={Design.NEUTRAL}
            >
              Clear
            </Button>
            <Button
              size={Size.LG}
              leadingElement={<MdAutoAwesome />}
              onClick={generate}
              data-testid='generate-button'
            >
              Generate Ads
            </Button>
          </div>
        </div>
        <CopyTigerAlert dialog={dialog} isLoading={isLoading} onHide={() => setDialog(undefined)} />
        {showModelModal && (
          <ModelModal
            createModelTableData={createModelTableData}
            models={models}
            setShowModelModal={setShowModelModal}
            showModelModal={showModelModal}
          />
        )}
      </div>
    </>
  );
};

// --- Private sub-components ---

interface ModelColumnProps {
  company: string;
  models: InstanceRow[] | undefined;
  selectedModel: string;
  setSelectedModel: (id: string) => void;
  setShowModelModal: (id: string) => void;
}

const ModelColumn = ({
  company,
  models,
  selectedModel,
  setSelectedModel,
  setShowModelModal
}: ModelColumnProps) => (
  <div className='column'>
    <div className='column-header'>
      <div className='column-title'>1. Model</div>
    </div>
    <div className='column-description'>
      Select a learning model enable AdCopy AI to write scalable, intelligent copywriting.
    </div>
    <div className='column-content'>
      {models && models.length ? (
        models.map(model => (
          <InteractiveDiv
            key={model.id}
            className={cn('style-guide-card', { selected: selectedModel === model.id })}
            onClick={() => setSelectedModel(model.id)}
          >
            <div className='card-radio'>
              <input
                type='radio'
                id={model.id}
                name='model'
                value={model.id}
                checked={selectedModel === model.id}
                onChange={e => setSelectedModel(e.target.value)}
              />
            </div>
            <div className='card-content'>
              <div className='card-title'>{model.name}</div>
              <div className='card-description'>
                {model.description ?? 'No description available'}
              </div>
              <div className='card-last-edited'>
                Last updated on {formatLastModified(model.lastModified, model.lastUser)}
              </div>
            </div>
            <InteractiveDiv
              className='card-info-icon'
              style={{ cursor: 'pointer' }}
              onClick={() => setShowModelModal(model.id)}
            >
              <MdInfoOutline />
            </InteractiveDiv>
          </InteractiveDiv>
        ))
      ) : (
        <div className='empty-state'>
          <div>No models found.</div>
          <div>
            Please create a model in the{' '}
            <a href={`/${company}/adcopy-ai/models`} className='style-guide-link'>
              Train Models tab
            </a>{' '}
            to get started.
          </div>
        </div>
      )}
    </div>
  </div>
);

interface StyleGuideColumnProps {
  company: string;
  selectedStyleGuide: string;
  setSelectedStyleGuide: (id: string) => void;
  styleGuides: StyleGuideRow[];
}

const StyleGuideColumn = ({
  company,
  selectedStyleGuide,
  setSelectedStyleGuide,
  styleGuides
}: StyleGuideColumnProps) => (
  <div className='column'>
    <div className='column-header'>
      <div className='column-title'>2. Style Guide</div>
    </div>
    <div className='column-description'>
      Select a Style Guide to further refine AI&apos;s understanding and ensure alignment with
      unique tone and messaging.
    </div>
    <div className='column-content'>
      {styleGuides && styleGuides.length ? (
        styleGuides.map(styleGuide => (
          <InteractiveDiv
            key={styleGuide.id}
            className={cn('style-guide-card', { selected: selectedStyleGuide === styleGuide.id })}
            onClick={() =>
              setSelectedStyleGuide(selectedStyleGuide === styleGuide.id ? '' : styleGuide.id)
            }
          >
            <div className='card-radio'>
              <input
                type='radio'
                id={styleGuide.id}
                name='styleGuide'
                value={styleGuide.id}
                checked={selectedStyleGuide === styleGuide.id}
                onChange={e => setSelectedStyleGuide(e.target.value)}
              />
            </div>
            <div className='card-content'>
              <div className='card-title'>{styleGuide.name}</div>
              <div className='card-description'>
                {styleGuide.description ?? 'No description available'}
              </div>
              <div className='card-last-edited'>
                Last updated on {formatLastModified(styleGuide.lastModified, styleGuide.lastUser)}
              </div>
            </div>
            <OverlayTrigger
              trigger={['hover', 'focus']}
              placement={Placement.RIGHT}
              overlay={
                <Popover>
                  <Popover.Body>
                    {styleGuide.description ?? 'No description available'}
                  </Popover.Body>
                </Popover>
              }
            >
              <div className='card-info-icon'>
                <MdInfoOutline />
              </div>
            </OverlayTrigger>
          </InteractiveDiv>
        ))
      ) : (
        <div className='empty-state'>
          <div>No style guides found.</div>
          <div>
            Please create a style guide in the{' '}
            <a href={`/${company}/adcopy-ai/style-guide`} className='style-guide-link'>
              Style Guide tab
            </a>{' '}
            to get started.
          </div>
        </div>
      )}
    </div>
  </div>
);

interface CreativityColumnProps {
  additionalInstructions: string;
  selectedCreativity: string;
  setAdditionalInstructions: (value: string) => void;
  setSelectedCreativity: (value: string) => void;
}

const CreativityColumn = ({
  additionalInstructions,
  selectedCreativity,
  setAdditionalInstructions,
  setSelectedCreativity
}: CreativityColumnProps) => (
  <div className='column'>
    <div className='column-header'>
      <div className='column-title'>3. Creativity</div>
    </div>
    <div className='column-description'>
      Select a creativity level to control the balance between strict adherence to your existing
      brand style and the generation of diverse ad copy variations.
    </div>
    <div className='column-content'>
      {Object.keys(CREATIVITY_OPTIONS).map(label => (
        <InteractiveDiv
          key={label}
          className={cn('style-guide-card', { selected: selectedCreativity === label })}
          onClick={() => setSelectedCreativity(label)}
        >
          <div className='card-radio'>
            <input
              type='radio'
              id={`creativity-${label}`}
              name='creativity'
              value={label}
              checked={selectedCreativity === label}
              onChange={e => setSelectedCreativity(e.target.value)}
            />
          </div>
          <div className='card-content'>
            <div className='card-title'>{label}</div>
            <div className='card-description'>{CREATIVITY_OPTIONS[label].description}</div>
          </div>
        </InteractiveDiv>
      ))}
      <div className='additional-instructions-section'>
        <div className='additional-instructions-label'>Optional Additional Instructions</div>
        <textarea
          className='additional-instructions-input'
          placeholder='e.g. Mention we offer a 10% discount.'
          value={additionalInstructions}
          onChange={e => setAdditionalInstructions(e.target.value)}
          rows={4}
          maxLength={500}
        />
        <div className='char-count'>{additionalInstructions.length}/500 characters</div>
      </div>
    </div>
  </div>
);

// Multi-select field for the Platform/Account/Campaign/Ad Group dropdowns below: a plain
// (non-chip) dropdown that fills the available width. Above SUMMARIZE_THRESHOLD selections, the
// value is a short "<count> <noun(s)>" summary rather than the joined list of names — a long
// joined string can't be relied on to ellipsis-truncate through this component's nested flex
// chain, so a large selection is summarized in JS instead of leaning on CSS to bound it. A
// checkbox per menu row shows which options are selected, and a scrollable tooltip reveals the
// real selected names whenever the value is summarized (or, for a couple of long names, actually
// CSS-truncated) — staying open while the mouse is over either the toggle OR the tooltip, since
// the small visual gap between them would otherwise close it as the mouse crosses. `onChange` is
// called with an id, or the 'All' sentinel.
const SUMMARIZE_THRESHOLD = 2;

// Rendering a removable tag per keyword is unusable for large clients, so show only the first N.
const MAX_VISIBLE_KEYWORD_TAGS = 200;
// Cap the names in the selected-values popover for the same reason.
const MAX_TOOLTIP_NAMES = 50;

interface IdMapMultiSelectDropdownProps {
  allLabel: string;
  disabled?: boolean;
  idMap: Record<string, string>;
  onChange: (value: string) => void;
  placeholder: string;
  selectedIds: Record<string, string>;
  // Append "(id)" to each option label.
  showIds?: boolean;
  singularNoun: string;
}

const IdMapMultiSelectDropdown = ({
  allLabel,
  disabled,
  idMap,
  onChange,
  placeholder,
  selectedIds,
  showIds = false,
  singularNoun
}: IdMapMultiSelectDropdownProps) => {
  // Everything below that is O(options) is memoized: this component re-renders on every hover of
  // the toggle, and for large clients (tens of thousands of ad groups) rebuilding the option arrays
  // each time makes the dropdown unusable.
  const allKeys = useMemo(() => Object.keys(idMap), [idMap]);
  const selectedKeys = useMemo(() => Object.keys(selectedIds), [selectedIds]);
  const formatOptionLabel = useCallback(
    (id: string) => (showIds ? `${idMap[id]} (${id})` : idMap[id]),
    [idMap, showIds]
  );
  const isAllSelected = allKeys.length > 0 && selectedKeys.length === allKeys.length;
  const isSummarized = selectedKeys.length > SUMMARIZE_THRESHOLD;
  const fullNamesText = useMemo(() => {
    const names = selectedKeys.slice(0, MAX_TOOLTIP_NAMES).map(formatOptionLabel).join(', ');
    const hidden = selectedKeys.length - MAX_TOOLTIP_NAMES;
    return hidden > 0 ? `${names} +${hidden.toLocaleString()} more` : names;
  }, [selectedKeys, formatOptionLabel]);
  const displayValue = isSummarized
    ? conditionallyPluralizeWithCount(selectedKeys.length, singularNoun)
    : fullNamesText;
  const [valueRef, isTruncated] = useIsTruncated<HTMLSpanElement>(displayValue);
  const options = useMemo(
    () => [
      { label: allLabel, value: 'All' },
      ...allKeys.map(id => ({ value: id, label: formatOptionLabel(id) }))
    ],
    [allLabel, allKeys, formatOptionLabel]
  );
  const menuItems = useMemo(
    () => [
      { label: allLabel, value: 'All', selected: isAllSelected },
      ...allKeys.map(id => ({
        value: id,
        label: formatOptionLabel(id),
        selected: selectedIds[id] != null
      }))
    ],
    [allLabel, allKeys, formatOptionLabel, isAllSelected, selectedIds]
  );
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [toggleHovered, setToggleHovered] = useState(false);
  const [popoverHovered, setPopoverHovered] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  return (
    <div
      className='id-map-multi-select-dropdown'
      ref={wrapperRef}
      onMouseEnter={() => setToggleHovered(true)}
      onMouseLeave={() => setToggleHovered(false)}
    >
      <Dropdown
        overlayPlacement={Placement.RIGHT}
        disabled={disabled}
        // hasTicks (implied by multiSelect) renders a checkbox per row. hideApplyOnClick/
        // hideCancelOnClick keep the existing click-to-toggle-and-close-immediately
        // behavior — without them, ticks alone would also pull in an Apply/Cancel footer.
        hideApplyOnClick
        hideCancelOnClick
        menuItems={menuItems}
        menuStrategy='fixed'
        multiSelect
        onChange={onChange}
        virtualize
        options={options}
        placeholder={placeholder}
        setShow={setDropdownOpen}
        show={dropdownOpen}
        size={Size.SM}
        value={displayValue}
        valueRef={valueRef}
      />
      <Overlay
        target={() => wrapperRef.current}
        show={!dropdownOpen && (isSummarized || isTruncated) && (toggleHovered || popoverHovered)}
        placement={Placement.BOTTOM}
      >
        <Popover
          className='truncated-multi-select-tooltip'
          onMouseEnter={() => setPopoverHovered(true)}
          onMouseLeave={() => setPopoverHovered(false)}
        >
          <Popover.Body>{fullNamesText}</Popover.Body>
        </Popover>
      </Overlay>
    </div>
  );
};

interface KeywordColumnProps {
  accountIDMap: Record<string, string>;
  adGroupIDMap: Record<string, string>;
  campaignIDMap: Record<string, string>;
  isLoadingTrainingOptions: boolean;
  keywordsData: KeywordsData[];
  platformIDMap: Record<string, string>;
  removeKeyword: (keyword: string) => void;
  selectedAccountIDs: Record<string, string>;
  selectedAdGroupIDs: Record<string, string>;
  selectedCampaignIDs: Record<string, string>;
  selectedPlatforms: Record<string, string>;
  setSelectedAccountIDs: (ids: Record<string, string>) => void;
  setSelectedAdGroupIDs: (ids: Record<string, string>) => void;
  setSelectedCampaignIDs: (ids: Record<string, string>) => void;
  setSelectedPlatforms: (ids: Record<string, string>) => void;
  uniqueKeywords: string[];
}

const KeywordColumnBase = ({
  accountIDMap,
  adGroupIDMap,
  campaignIDMap,
  isLoadingTrainingOptions,
  keywordsData,
  platformIDMap,
  removeKeyword,
  selectedAccountIDs,
  selectedAdGroupIDs,
  selectedCampaignIDs,
  selectedPlatforms,
  setSelectedAccountIDs,
  setSelectedAdGroupIDs,
  setSelectedCampaignIDs,
  setSelectedPlatforms,
  uniqueKeywords
}: KeywordColumnProps) => (
  <div className='column'>
    <div className='column-header'>
      <div className='column-title'>4. Keywords</div>
    </div>
    <div className='column-description'>
      Select the campaigns and ad groups where new ads are needed. AdCopy AI will automatically
      generate a single, optimized RSA for each corresponding ad group.
    </div>
    <div className='column-content'>
      {isLoadingTrainingOptions ? (
        <div className='loading-state'>
          <Spinner />
        </div>
      ) : (
        <div className='keywords-section'>
          <div className='account-info'>
            {Object.keys(platformIDMap).length > 0 && (
              <div className='dropdown'>
                <div className='label'>Platform:</div>
                <IdMapMultiSelectDropdown
                  allLabel='All Platforms'
                  idMap={platformIDMap}
                  onChange={value => {
                    if (value === 'All') {
                      setSelectedPlatforms(
                        Object.keys(selectedPlatforms).length === Object.keys(platformIDMap).length
                          ? {}
                          : platformIDMap
                      );
                    } else if (selectedPlatforms[value] == null) {
                      setSelectedPlatforms({ ...selectedPlatforms, [value]: platformIDMap[value] });
                    } else {
                      const { [value]: _, ...rest } = selectedPlatforms;
                      setSelectedPlatforms(rest);
                    }
                    // Platform is the top of the hierarchy; changing it invalidates downstream picks.
                    setSelectedAccountIDs({});
                    setSelectedCampaignIDs({});
                    setSelectedAdGroupIDs({});
                  }}
                  placeholder='Select a Platform'
                  selectedIds={selectedPlatforms}
                  singularNoun='Platform'
                />
              </div>
            )}
            <div className='dropdown'>
              <div className='label'>Account:</div>
              <IdMapMultiSelectDropdown
                allLabel='All Accounts'
                showIds
                idMap={accountIDMap}
                onChange={value => {
                  if (value === 'All') {
                    setSelectedAccountIDs(accountIDMap);
                  } else if (selectedAccountIDs[value] == null) {
                    setSelectedAccountIDs({
                      ...selectedAccountIDs,
                      [value]: accountIDMap[value]
                    });
                  } else {
                    const { [value]: _, ...rest } = selectedAccountIDs;
                    setSelectedAccountIDs(rest);
                  }
                }}
                placeholder='Select an Account'
                selectedIds={selectedAccountIDs}
                singularNoun='Account'
              />
            </div>
            <div className='dropdown'>
              <div className='label'>Campaign:</div>
              <IdMapMultiSelectDropdown
                allLabel='All Campaigns'
                disabled={Object.keys(selectedAccountIDs).length === 0}
                showIds
                idMap={campaignIDMap}
                onChange={value => {
                  if (value === 'All') {
                    setSelectedCampaignIDs(
                      Object.keys(selectedCampaignIDs).length === Object.keys(campaignIDMap).length
                        ? {}
                        : campaignIDMap
                    );
                    setSelectedAdGroupIDs({});
                  } else if (selectedCampaignIDs[value] == null) {
                    setSelectedCampaignIDs({
                      ...selectedCampaignIDs,
                      [value]: campaignIDMap[value]
                    });
                  } else {
                    const { [value]: _, ...rest } = selectedCampaignIDs;
                    setSelectedCampaignIDs(rest);
                  }
                }}
                placeholder='Select a Campaign'
                selectedIds={selectedCampaignIDs}
                singularNoun='Campaign'
              />
            </div>
            <div className='dropdown'>
              <div className='label'>Ad Group:</div>
              <IdMapMultiSelectDropdown
                allLabel='All Ad Groups'
                disabled={Object.keys(selectedCampaignIDs).length === 0}
                showIds
                idMap={adGroupIDMap}
                onChange={value => {
                  if (value === 'All') {
                    setSelectedAdGroupIDs(
                      Object.keys(selectedAdGroupIDs).length === Object.keys(adGroupIDMap).length
                        ? {}
                        : adGroupIDMap
                    );
                  } else if (selectedAdGroupIDs[value] == null) {
                    setSelectedAdGroupIDs({
                      ...selectedAdGroupIDs,
                      [value]: adGroupIDMap[value]
                    });
                  } else {
                    const { [value]: _, ...rest } = selectedAdGroupIDs;
                    setSelectedAdGroupIDs(rest);
                  }
                }}
                placeholder='Select an Ad Group'
                selectedIds={selectedAdGroupIDs}
                singularNoun='Ad Group'
              />
            </div>
          </div>
          <div className='keyword-tags'>
            {uniqueKeywords.slice(0, MAX_VISIBLE_KEYWORD_TAGS).map((keyword, index) => (
              <span key={index} className='tag'>
                {keyword}
                <IconButton
                  className='remove-tag'
                  appearance={Appearance.EMPTY}
                  onClick={() => removeKeyword(keyword)}
                  title='Remove keyword'
                >
                  <MdClose />
                </IconButton>
              </span>
            ))}
            {uniqueKeywords.length > MAX_VISIBLE_KEYWORD_TAGS && (
              <span className='empty-state'>
                +{(uniqueKeywords.length - MAX_VISIBLE_KEYWORD_TAGS).toLocaleString()} more keywords
                selected
              </span>
            )}
            {keywordsData.length === 0 && (
              <span className='empty-state'>
                Keywords will appear here when you select an account, campaign, and ad group
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  </div>
);

// Memoized so typing in unrelated columns doesn't re-render the option-heavy dropdowns.
const KeywordColumn = memo(KeywordColumnBase);

interface ModelModalProps {
  createModelTableData: (model: InstanceRow) => {
    headers: { name: string; label: string; flex: number; width?: number }[];
    data: Record<string, unknown>[];
  };
  models: InstanceRow[] | undefined;
  setShowModelModal: (id: string | null) => void;
  showModelModal: string;
}

const ModelModal = ({
  createModelTableData,
  models,
  setShowModelModal,
  showModelModal
}: ModelModalProps) => {
  const modelsList = models ?? [];
  const model = modelsList.find(m => m.id === showModelModal);

  return (
    <InteractiveDiv className='copytiger-modal-backdrop' onClick={() => setShowModelModal(null)}>
      <div
        className='copytiger-modal-content'
        role='button'
        tabIndex={0}
        onClick={e => e.stopPropagation()}
        onKeyDown={e => e.stopPropagation()}
      >
        <div className='copytiger-modal-header'>
          <div className='copytiger-modal-title-container'>
            <div className='copytiger-modal-title'>
              {modelsList.find(m => m.id === showModelModal)?.name ?? 'Model Instance'}
            </div>
            <div className='text'>Ads used in this model instance</div>
          </div>
          <IconButton
            className='copytiger-modal-close-button'
            appearance={Appearance.EMPTY}
            onClick={() => setShowModelModal(null)}
          >
            <MdClose />
          </IconButton>
        </div>
        <div className='copytiger-modal-table-container'>
          {model
            ? (() => {
                const { headers, data } = createModelTableData(model);
                return (
                  <BPMTable headers={headers} data={data} pagination={false} filterBar={false} />
                );
              })()
            : null}
        </div>
      </div>
    </InteractiveDiv>
  );
};
